<!-- employee-registration-page.component.html -->
<app-page-layout
  [title]="isEditMode() ? 'Update Profile' : 'Add a New Team Member'"
  [subtitle]="isEditMode() ? 'Keep this person\'s information accurate and up to date.' : 'Fill in the details to bring someone new on board.'"
  [breadcrumbKey]="isEditMode() ? 'employeeEdit' : 'employeeRegistration'"
  [flatContent]="true">

  <!-- ── Main Split Layout ─────────────────────────────────────────────────── -->
  <div page-content class="flex flex-col lg:flex-row min-h-[calc(100vh-230px)]">

    <!-- ════════════════════════════════════════════════════════════════
         LEFT PANEL — Vertical Step Navigation (25%)
         On mobile: horizontal scrollable row at the top
    ════════════════════════════════════════════════════════════════ -->
    <app-vertical-stepper
      [steps]="steps"
      [activeIndex]="activeIndex()"
      [maxVisitedStep]="maxVisitedStep()"
      [stepStates]="stepCompletionState()"
      (stepClick)="goToStep($event)"
      class="block">
    </app-vertical-stepper>

    <!-- ════════════════════════════════════════════════════════════════
         RIGHT PANEL — Form Content Area (75%)
    ════════════════════════════════════════════════════════════════ -->
    <main class="flex-1 min-w-0 flex flex-col overflow-y-auto">

      <!-- Loading state -->
      @if (isLoading()) {
        <div class="flex flex-col items-center justify-center flex-1 gap-4 p-12">
          <i class="pi pi-spin pi-spinner text-3xl text-primary"></i>
          <p class="text-sm text-surface-500">Loading registration form...</p>
        </div>
      }

      <!-- Error state -->
      @else if (loadError()) {
        <div class="flex flex-col items-center justify-center flex-1 gap-4 p-12 max-w-lg mx-auto text-center">
          <app-alert
            severity="error"
            title="Error Loading Registration Form"
            [description]="loadError()!"
            class="block w-full">
          </app-alert>
          <p-button label="Retry" severity="secondary" [outlined]="true" (onClick)="retryLoad()"></p-button>
        </div>
      }

      <!-- Form (only rendered once config is loaded) -->
      @else {
      <form [formGroup]="employeeForm" class="flex flex-col flex-1 gap-0">

        <!-- ── Step Content ─────────────────────────────────────────── -->
        <div class="flex-1 p-4 sm:p-4 lg:px-6 lg:py-0 lg:pb-3">

          <!-- Step header (title + description) -->
          <div class="mb-5">
            <h2 class="text-xl font-bold text-surface-900 dark:text-surface-0 tracking-tight mb-1">
              {{ currentStep.title }}
            </h2>
            <p class="text-sm text-surface-500 dark:text-surface-400 mt-0.5">
              Fill in the details for this section.
            </p>
          </div>

          <!-- ── Standard Steps: render sections via DynamicFormRenderer ── -->
          @if (currentStep.sections.length > 0) {
            <div class="animate-in fade-in slide-in-from-right-3 duration-250">
              <app-dynamic-form-renderer [sections]="currentStep.sections">
              </app-dynamic-form-renderer>
            </div>
          }

          <!-- ── Review Step: custom summary ─────────────────────────── -->
          @if (currentStep.sections.length === 0 && activeIndex() === steps.length - 1) {
            <div class="flex flex-col gap-0 animate-in fade-in slide-in-from-right-3 duration-250">

              <!-- Review banner -->
              <app-alert
                severity="info"
                title="Almost done — take one last look"
                description="Please verify all the information below is accurate before final submission."
                class="block mb-4">
              </app-alert>

              <!-- Review content -->
              <app-dynamic-view-renderer
                [config]="formConfig()!"
                [data]="employeeForm.getRawValue()">
              </app-dynamic-view-renderer>

            </div>
          }

        </div>

        <!-- ── Footer Navigation Bar ────────────────────────────────── -->
        <div class="sticky bottom-0 flex items-center justify-between
                    px-4 sm:px-5 lg:px-6 py-3 bg-transparent">

          <!-- Left: Cancel + Save as Draft -->
          <div class="flex items-center gap-3">
            <p-button
              label="Cancel"
              [outlined]="true"
              severity="secondary"
              (onClick)="onCancel()">
            </p-button>

            @if (!isEditMode()) {
              <p-button
                label="Save as Draft"
                [outlined]="true"
                severity="secondary"
                [loading]="isSavingDraft()"
                [disabled]="!canSaveDraft()"
                pTooltip="Enter Full Name to save a draft"
                [tooltipDisabled]="canSaveDraft()"
                tooltipPosition="top"
                (onClick)="onSaveDraft()">
              </p-button>
            }
          </div>

          <!-- Right: Back + Next / Submit -->
          <div class="flex items-center gap-3">
            <!-- Back button -->
            @if (!isFirstStep()) {
              <p-button
                label="Back"
                [outlined]="true"
                severity="secondary"
                (onClick)="previous()">
              </p-button>
            }

            <!-- Next (non-last steps) -->
            @if (!isLastStep()) {
              <p-button
                label="Next"
                (onClick)="next()">
              </p-button>
            }

            <!-- Submit (last step) -->
            @if (isLastStep()) {
              <p-button
                [label]="isSubmitting() ? (isEditMode() ? 'Saving…' : 'Adding…') : (isEditMode() ? 'Save Profile' : 'Add Person')"
                severity="success"
                (onClick)="onSubmit()"
                [loading]="isSubmitting()"
                [disabled]="employeeForm.invalid || isSubmitting()">
              </p-button>
            }
          </div>
        </div>

      </form>
      } <!-- end @else (form loaded) -->
    </main>

  </div>

</app-page-layout>
